import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable, Optional, SkipSelf } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { RWDService } from 'src/app/view/user-context/services/rwd.service';
import { ConfirmationData, ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';
import { RangedReportDialogComponent } from '../components/ranged-report-dialog/ranged-report-dialog.component';
import { RangedReportBase } from '../classes/ranged-report';
import { DateConfirmationData, DateConfirmationDialogComponent } from '../components/date-confirmation-dialog/date-confirmation-dialog.component';
import { AsyncHlp } from '../helpers/async.helper';

export interface DialogConfig {
  width?: string;
  height?: string;
  minWidth?: string;
  minHeight?: string;
  maxWidth?: string;
  maxHeight?: string;
  /** @deprecated 14.0.0 Remove boolean option from autoFocus. Use string or AutoFocusTarget instead */
  autoFocus?: boolean;
  // TODO: openAutoDialog() and replace all auto-sized dialogs
  autoSize?: boolean;
  panelClass?: string | string[];
  disableClose?: boolean;
  position?: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  constructor(
    private dialog: MatDialog,
    private rwdService: RWDService,
    @Optional() @SkipSelf() thisService?: DialogService
  ) {
    if (thisService) throw new Error('Multiple instances of DialogService');
  }

  // TODO: create interface DialogComponent<S, T> fo force S and T types without explicitly passing type parameters
  public openSimpleDialog<T, S = any, R = any>(
    component: ComponentType<T>,
    data: S,
    additionalConfig?: DialogConfig
  ): MatDialogRef<T, R> {
    return this.dialog.open(component, {
      maxWidth: '95vw',
      width: additionalConfig?.autoSize ? 'auto' : this.rwdService.isSmallDevice ? '95vw' : '60vw',
      height: additionalConfig?.autoSize ? 'auto' : '90vh',
      maxHeight: '90vh',
      ...additionalConfig,
      data
    });
  }

  public openPanelDialog<T, R = any>(
    component: ComponentType<T>,
    data: R,
    additionalConfig?: DialogConfig
  ): MatDialogRef<T, any> {
    const width = this.rwdService.isSmallDevice ? '100%' : '50%';
    return this.dialog.open(component, {
      minWidth: '350px',
      width,
      maxWidth: width,
      height: '100%',
      position: { right: '0', top: '0' },
      ...additionalConfig,
      data
    });
  }

  public openConfirmationDialog(data: ConfirmationData, additionalConfig?: DialogConfig): Observable<boolean> {
    return this.dialog
      .open(ConfirmationDialogComponent, {
        maxWidth: '500px',
        maxHeight: 'auto',
        height: 'auto',
        data,
        ...additionalConfig
      })
      .afterClosed()
      .pipe(map((result) => !!result));
  }

  public openDateConfirmationDialog(data: DateConfirmationData, additionalConfig?: DialogConfig): Promise<Date | null> {
    return AsyncHlp.onFirst(this.dialog
      .open(DateConfirmationDialogComponent, {
        maxWidth: '500px',
        maxHeight: 'auto',
        height: 'auto',
        data,
        ...additionalConfig
      })
      .afterClosed());
  }

  /**
   * Open and initialize RangedReportDialog
   * @param {Constructor<T>} factory
   * @param {U}              componentData
   * @return {MatDialogRef}
   */
  public openRangedReportDialog<T extends RangedReportBase<U>, U>(
    factory: ComponentType<T>,
    componentData: U
  ): MatDialogRef<RangedReportDialogComponent<any, any>, void> {
    // FIXME: <any, any> because Angular's 13 typescript is stupid
    return this.dialog.open(RangedReportDialogComponent, {
      width: '500px',
      height: 'auto',
      data: { factory, compData: componentData }
    });
  }

  public closeAllDialogs(): void {
    this.dialog.closeAll();
  }
}
